import Agents from "../components/agents.tsx";
import { Header } from "../components/ui/header.jsx";
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Footer } from "../components/ui/footer.jsx";
import { money } from "../utilities/currency.js";
import { getAgentDisplayPicUrl } from "../utilities/conversions.js";
import { useQuery } from "@tanstack/react-query";
import { RootState } from "../state/store.ts";
import { useSelector } from "react-redux";

const T = {
  offer: "✨ Spesialtilbud – Betal 99 kr, få 198 kr i ringe kreditt! ✨",
  signIn: "Logg inn",
  join: "Bli med",
  allAdvisors: "Alle veiledere",
  festivalTitle: "Festivalkampanjer",
  referTitle: "Verv & få gratis reading!",
  referBody: "Verv 5 betalende kunder og få en GRATIS 15‑min reading hos en toppveileder.",
  blogTitle: "Fra Mystic‑bloggen",
  viewAll: "Se alle",
  subscribeLine: "Få nye guider og tilbud på e‑post.",
  read: "Les",
  invite: "Inviter venner",
  sponsored: "Sponset",
  ad: (n: number) => `Annonse ${n}`,
  adBlurb: "Annonser produkter, apper eller arrangementer her.",
  learnMore: "Les mer",
  connectNow: "Koble til nå",
  from: "fra",
  minutes: "min",
  verified: "Verifisert",
  categories: [
    "Kjærlighetsreading",
    "Klarsynt reading",
    "Tarot‑reading",
    "Astrologi & Horoskop",
    "Englebudskap",
    "Orakel‑veiledning",
    "Drømmetydning",
    "Åndelig medium",
  ],
  hero: {
    name: "Advokatene",
    tag: "Kjærlighetsekspert",
    blurb:
      "Mitt løfte: Jeg er her for deg. Når det er vanskelig å vite hvem man kan stole på, er ekte nærvær alt.",
    getReading: "Bestill konsultasjon",
  },
  footer: {
    getApp: "Få appen for klarhet hvor du enn er.",
    explore: "Utforsk",
    special: "Spesialiteter",
    more: "Mer",
    advisors: "Veiledere",
    blog: "Bloggen vår",
    applyCode: "Bruk kampanjekode",
    support: "Kundesupport",
    about: "Om oss",
    apply: "Veileder? Søk her",
    privacy: "Personvern",
    terms: "Vilkår",
  },
  wallet: {
    title: "Kjøp ringeminutter",
    intro:
      "Minutter lagres i din Mystic‑wallet og kan brukes hos alle veiledere (chat / samtale / video).",
    numLabel: "Telefonnummeret du ringer fra",
    amount: "Velg beløp",
    buy: (amt: number) => `Kjøp ${money(amt)} kreditt`,
    learn: "Les mer om betalingsmåter",
    err: "Skriv inn et gyldig telefonnummer",
    otp: (n: string) => `Kode sendt til ${n}. Skriv den inn for å bekrefte.`,
    enterOtp: "Skriv inn kode",
    verify: "Bekreft & legg til kreditt",
    cancel: "Avbryt",
    success: (amt: number) => `${money(amt)} lagt til i lommeboken!`,
    pm1: "Vipps MobilePay",
    pm2: "Kort (krever registrering på ditt mobilnummer)",
    nolgin: "Ingen innlogging nødvendig for Vipps MobilePay – ring direkte.",
  },
};

type AgentsPageProps = {
  baseUrl: string,
  currentUser: any,
  onLoginClick: any,
  onLoginSuccess: any,
  onLogout: any,
}

export default function AgentsPage({ baseUrl, currentUser, onLoginClick, onLoginSuccess, onLogout }: AgentsPageProps) {
  const [scrolled, setScrolled] = useState(false);
  const [wallet, setWallet] = useState(0);
  const client = useSelector((state: RootState) => state.client.client);
  useEffect(() => {
    if (currentUser?.role === "customer") {
      axios.get(baseUrl + "/getCustomerWallet", { params: { token: currentUser.token } })

        .then((response) => {
          if (response.data.status === "OK") {
            setWallet(response.data.data.remaining_amount);
          }
        });
    }
  }, [currentUser, baseUrl])

  // --- API functions ---
  const fetchAgents = async (baseUrl: string) => {
    if (client) {
      const response = await axios.get(baseUrl + "/getAgents", { params: { client_uid: client?.uid } });
      return response.data.data;
    }
  };

  const fetchStatuses = async (baseUrl: string) => {
    if (client) {
      const response = await axios.get(baseUrl + "/getAgentsStatuses", { params: { client_uid: client?.uid } });
      return response.data.data; // [{ uid, call_status }]
    }
  };
  // --- React Query: fetch agents once ---
  const { data: agents } = useQuery({
    queryKey: ["agents"],
    queryFn: () => fetchAgents(baseUrl),
    staleTime: Infinity,
    enabled: !!client, // only run if client is available
  });

  // --- React Query: poll only statuses ---
  const { data: statuses } = useQuery({
    queryKey: ["agent-statuses"],
    queryFn: () => fetchStatuses(baseUrl),
    refetchInterval: 5000, // 5 sec poll
    enabled: !!client,
  });

  // --- Merge full agent data with statuses ---
  const advisors = useMemo(() => {
    if (!agents) return [];

    return agents.map((agent: any) => {
      // safe copies / computed fields (avoid mutating original)
      const display_pic = getAgentDisplayPicUrl(agent.display_pic ?? null, baseUrl);

      // parse created_at -> created_year & day_month (defensive)
      let created_year = agent.created_year ?? "";
      let day_month = agent.day_month ?? "";
      if (agent.created_at) {
        try {
          const datePart = String(agent.created_at).split(" ")[0]; // "YYYY-MM-DD"
          const [year, month, day] = datePart.split("-");
          if (year && month && day) {
            created_year = year;
            day_month = `${day}${month}`;
          }
        } catch (e) {
          // leave existing values if parsing fails
        }
      }

      // ensure minute price is a number
      let customer_minute_price = agent.customer_minute_price;
      if (typeof customer_minute_price === "string") {
        const parsed = parseFloat(customer_minute_price);
        customer_minute_price = isNaN(parsed) ? 0 : parsed;
      }

      // find latest status from statuses list (if present)
      const statusObj = statuses?.find((s) => s.uid === agent.uid);
      const call_status = statusObj?.call_status ?? agent.call_status;

      return {
        ...agent,
        display_pic,
        created_year,
        day_month,
        customer_minute_price,
        call_status,
      };
    });
  }, [agents, statuses, baseUrl]);


  const [filteredAdvisors, setFilteredAdvisors] = useState([]);

  useEffect(() => {
    if (advisors) {
      setFilteredAdvisors(advisors);
    }
  }, [advisors]);

  const handleChipClick = (skill: any) => {
    const keyword = skill.toLowerCase();

    const results = advisors.filter(advisor =>
      advisor.skills?.some(s =>
        s?.toLowerCase().includes(keyword)
      )
    );

    setFilteredAdvisors(results);
  };

  const [searchField, setSearchField] = useState("");
  const onSearchClick = () => {
    if (!searchField.trim()) {
      setFilteredAdvisors(advisors);  // reset if empty
      return;
    }

    const keyword = searchField.toLowerCase();

    const results = advisors.filter((a: any) =>
      a.name?.toLowerCase().includes(keyword)
    );

    setFilteredAdvisors(results);
  }

  useEffect(() => {
    if (!searchField) {
      onSearchClick();
    }
  }, [searchField]);

  const theme_settings = client ? JSON.parse(client.theme_settings) : {};

  const primaryColor = theme_settings.primaryColor || "#7c3aed"; // default purple-700
  const secondaryColor = theme_settings.secondaryColor || "#ec4899";

  const [appearance, setAppearance] = useState({
    gradientEnabled: false,
    gradientFrom: `${primaryColor}`,
    gradientTo: `${secondaryColor}`,
  });

  useEffect(() => {
    setAppearance(prev => ({
      gradientFrom: theme_settings.primaryColor,
      gradientTo: theme_settings.secondaryColor,
      gradientEnabled: theme_settings.useGradient === "true",
    }));
  }, [theme_settings.useGradient]);

  return (
    <div style={{
      "--primary-color": primaryColor,
      "--secondary-color": secondaryColor,
    }} className={"bg-white text-gray-900"}>
      <Header wallet={wallet} scrolled={scrolled} T={T} onLoginClick={onLoginClick} currentUser={currentUser} onLogout={onLogout} baseUrl={baseUrl} />
      <Agents baseUrl={baseUrl} T={T} currentUser={currentUser} onLoginClick={onLoginClick} onLoginSuccess={onLoginSuccess} searchField={searchField} setSearchField={setSearchField} onSearchClick={onSearchClick} filteredAdvisors={filteredAdvisors} from={"agentspage"} />
      <Footer baseUrl={baseUrl} />
    </div>
  )
}